/** 平台图片资源 URL 解析（对齐 perseus @core/platform-resource ADR-0036）：
 *  纯函数、零依赖（不引 lib/utils/platformImage 以避免其 React 依赖链进入测试/纯函数场景） */

const SHOPEE_IMAGE_CDN_ORIGIN = 'https://down-sg.img.susercontent.com';
/** fett isShopeePic 同族判据：`.shopee.` 或 `.img.susercontent.com` 主机 */
const SHOPEE_PIC_PATTERN = /^.+(\.shopee\.|\.img\.susercontent\.com).+/;
const LAZADA_PIC_PATTERN = /^.+\.slatic\.net\/.+/;
/** 自足资源判据：scheme / 协议相对 / data: / blob:；之外非空值在 Shopee 平台按裸资源 id 处理 */
const SELF_CONTAINED_SRC_PATTERN = /^(?:(?:https?:)?\/\/|data:|blob:)/i;

const isBase64 = (src: string) => /^data:/.test(src);

const isShopeePic = (src: string) =>
  src ? !isBase64(src) && SHOPEE_PIC_PATTERN.test(src) : false;

const isLazadaPic = (src: string) =>
  src ? !isBase64(src) && LAZADA_PIC_PATTERN.test(src) : false;

const http2https = (url: string) =>
  url.startsWith('http://') ? `https://${url.slice('http://'.length)}` : url;

/** Shopee 裸资源 id → susercontent CDN 地址；自足资源原样返回 */
const expandShopeeResourceId = (src: string) =>
  !src || SELF_CONTAINED_SRC_PATTERN.test(src) ? src : `${SHOPEE_IMAGE_CDN_ORIGIN}/${src}`;

/** Lazada slatic 原图切海外 CDN 压缩 webp（fett lazadaPicCompress 口径）；非 Lazada 资源原样返回 */
const lazadaPicCompress = (src: string) => {
  if (!isLazadaPic(src)) return src;
  try {
    const url = new URL(src);
    url.pathname = `${url.pathname}_128x128.webp`;
    url.host = 'img-ovs.alicdn.com';
    return url.href;
  } catch {
    return src;
  }
};

export interface PlatformImageResource {
  /** 恒为原链（http→https 升级） */
  fullSrc: string;
  /** 平台压缩规则产物 */
  thumbnailSrc: string;
}

/** 平台图片资源解析主入口：Shopee = 裸资源 id 展开 + 主机门控 `_tn`；
 *  Lazada = 海外 CDN 128px webp；其余平台仅 http→https */
export const resolvePlatformImageResource = (
  source: string,
  platform?: string | null,
): PlatformImageResource => {
  const upper = platform?.toUpperCase();
  const fullSrc = http2https(upper === 'SHOPEE' ? expandShopeeResourceId(source) : source);
  let thumbnailSrc = fullSrc;
  if (upper === 'SHOPEE' && isShopeePic(fullSrc) && !fullSrc.endsWith('_tn')) {
    thumbnailSrc = `${fullSrc}_tn`;
  } else if (upper === 'LAZADA' && isLazadaPic(fullSrc)) {
    thumbnailSrc = lazadaPicCompress(fullSrc);
  }
  return { fullSrc, thumbnailSrc };
};
